import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Heart, Download, Database, FileJson, FileText, Table2, 
  ExternalLink, Calendar, Hash, ChevronRight, Code
} from 'lucide-react';
import { toast } from 'sonner';

const DATASETS = [
  {
    id: 'instituicoes',
    name: 'Instituições Parceiras',
    description: 'Lista de todas as instituições ativas na rede ENTRAJUDA com localização e respostas sociais.',
    endpoint: '/api/public/dados-abertos/instituicoes',
    fields: ['nome', 'tipo', 'distrito', 'concelho', 'valencias_ativas', 'total_utentes'],
    formats: ['JSON'],
    license: 'CC BY 4.0'
  },
  {
    id: 'distritos',
    name: 'Distritos e Concelhos',
    description: 'Lista de distritos e concelhos de Portugal com identificadores.',
    endpoint: '/api/distritos',
    fields: ['id', 'nome', 'codigo'],
    formats: ['JSON'],
    license: 'CC BY 4.0'
  },
  {
    id: 'valencias',
    name: 'Tipos de Respostas Sociais',
    description: 'Catálogo de respostas sociais e serviços prestados pelas instituições.',
    endpoint: '/api/valencia-categorias',
    fields: ['id', 'nome', 'descricao'],
    formats: ['JSON'],
    license: 'CC BY 4.0'
  },
  {
    id: 'mapa',
    name: 'Distribuição Geográfica',
    description: 'Contagem de instituições por distrito com coordenadas para mapas.',
    endpoint: '/api/dashboard/mapa-instituicoes',
    fields: ['distrito', 'count', 'lat', 'lng'],
    formats: ['JSON'],
    license: 'CC BY 4.0'
  }
];

export default function PublicDadosAbertosPage() {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState({});

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/stats`);
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleDownload = async (dataset) => {
    setLoading(prev => ({ ...prev, [dataset.id]: true }));
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${dataset.endpoint}`);
      if (response.ok) {
        const data = await response.json();
        const jsonStr = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `entrajudata_${dataset.id}_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`Download de ${dataset.name} iniciado`);
      }
    } catch (error) {
      toast.error('Erro ao fazer download');
    } finally {
      setLoading(prev => ({ ...prev, [dataset.id]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="public-dados-abertos-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/publico" className="flex items-center gap-2">
              <Heart className="w-8 h-8 text-[#0066CC]" fill="#0066CC" />
              <span className="text-xl font-bold text-slate-900">ENTRAJUDATA</span>
            </Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/publico" className="text-slate-600 hover:text-[#0066CC] transition-colors">Início</Link>
              <Link to="/publico/dados-abertos" className="text-[#0066CC] font-medium">Dados Abertos</Link>
              <Link to="/publico/instituicoes" className="text-slate-600 hover:text-[#0066CC] transition-colors">Instituições</Link>
              <Link to="/publico/voluntariado" className="text-slate-600 hover:text-[#0066CC] transition-colors">Ser Voluntário</Link>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/portal/login">
                <Button variant="outline" size="sm">Portal Instituições</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-green-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <Badge className="bg-green-100 text-green-700 mb-4">Open Data</Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900">
              Dados <span className="text-green-600">Abertos</span>
            </h1>
            <p className="mt-4 text-xl text-slate-600">
              Acesso livre a datasets da rede de solidariedade social portuguesa. 
              Use estes dados para investigação, análise ou desenvolvimento.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-3xl font-bold text-slate-900">{stats.instituicoes_ativas || 0}</p>
              <p className="text-slate-600">Instituições</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{stats.total_utentes?.toLocaleString('pt-PT') || 0}</p>
              <p className="text-slate-600">Utentes</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">18</p>
              <p className="text-slate-600">Distritos</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{DATASETS.length}</p>
              <p className="text-slate-600">Datasets</p>
            </div>
          </div>
        </div>
      </section>

      {/* Datasets */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900">Datasets Disponíveis</h2>
            <p className="text-slate-600 mt-2">
              Todos os datasets estão disponíveis em formato JSON com licença Creative Commons.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {DATASETS.map(dataset => (
              <Card key={dataset.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <Database className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{dataset.name}</CardTitle>
                        <Badge variant="outline" className="mt-1 text-xs">{dataset.license}</Badge>
                      </div>
                    </div>
                  </div>
                  <CardDescription className="mt-2">
                    {dataset.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Campos disponíveis:</p>
                      <div className="flex flex-wrap gap-1">
                        {dataset.fields.map(field => (
                          <Badge key={field} variant="secondary" className="text-xs font-mono">
                            {field}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Code className="w-4 h-4" />
                      <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">
                        GET {dataset.endpoint}
                      </code>
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button 
                        size="sm" 
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={() => handleDownload(dataset)}
                        disabled={loading[dataset.id]}
                      >
                        {loading[dataset.id] ? (
                          'A transferir...'
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-2" />
                            Download JSON
                          </>
                        )}
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${dataset.endpoint}`, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* API Documentation */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <Badge className="bg-green-500 text-white mb-4">API</Badge>
            <h2 className="text-3xl font-bold">Documentação da API</h2>
            <p className="text-slate-400 mt-2">
              Aceda aos dados programaticamente através da nossa API REST.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-slate-800 rounded-lg p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Code className="w-5 h-5 text-green-400" />
                Exemplo de Request
              </h3>
              <pre className="bg-slate-950 rounded p-4 text-sm overflow-x-auto">
                <code className="text-green-400">
{'curl -X GET \\\n  "' + process.env.REACT_APP_BACKEND_URL + '/api/public/stats"\n\n# Response:\n{\n  "instituicoes_ativas": ' + (stats.instituicoes_ativas || 0) + ',\n  "total_utentes": ' + (stats.total_utentes || 0) + ',\n  "total_voluntarios": ' + (stats.total_voluntarios || 0) + ',\n  ...\n}'}
                </code>
              </pre>
            </div>

            <div className="bg-slate-800 rounded-lg p-6">
              <h3 className="font-semibold mb-4">Endpoints Públicos</h3>
              <div className="space-y-3">
                <EndpointItem method="GET" path="/api/public/stats" desc="Estatísticas gerais" />
                <EndpointItem method="GET" path="/api/public/instituicoes" desc="Lista de instituições" />
                <EndpointItem method="GET" path="/api/public/missoes" desc="Missões de voluntariado" />
                <EndpointItem method="GET" path="/api/distritos" desc="Distritos de Portugal" />
                <EndpointItem method="GET" path="/api/dashboard/mapa-instituicoes" desc="Dados para mapa" />
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-slate-400 text-sm">
              Rate limit: 100 requests/minuto • Sem autenticação necessária para endpoints públicos
            </p>
          </div>
        </div>
      </section>

      {/* License */}
      <section className="py-16 bg-green-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Licença de Uso</h2>
          <p className="text-slate-600 mb-6">
            Todos os datasets são disponibilizados sob licença <strong>Creative Commons Attribution 4.0 International (CC BY 4.0)</strong>.
            Pode usar, partilhar e adaptar os dados para qualquer finalidade, incluindo comercial, desde que atribua crédito à ENTRAJUDA.
          </p>
          <a 
            href="https://creativecommons.org/licenses/by/4.0/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center text-green-600 hover:underline"
          >
            Ver licença completa
            <ExternalLink className="w-4 h-4 ml-1" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link to="/publico" className="inline-flex items-center gap-2 mb-4">
            <Heart className="w-6 h-6 text-[#0066CC]" fill="#0066CC" />
            <span className="font-bold">ENTRAJUDATA</span>
          </Link>
          <p className="text-slate-400 text-sm">© {new Date().getFullYear()} ENTRAJUDA. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
}

function EndpointItem({ method, path, desc }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <Badge className="bg-green-600 text-white font-mono">{method}</Badge>
      <code className="text-slate-300 flex-1 truncate">{path}</code>
      <span className="text-slate-500 text-xs hidden sm:inline">{desc}</span>
    </div>
  );
}
